import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@noor/api';
import { productApi, type RecommendedProduct } from '@noor/eats-api';
import { toast } from '@noor/lib';
import { XIcon } from 'lucide-react';

const MAX_RECOMMENDATIONS = 20;

export function RecommendationsSection({ productId }: { productId: string }) {
  const { t } = useTranslation('eats');
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<RecommendedProduct[] | null>(null);
  const recommendations = useQuery({
    queryKey: ['merchant-product-recommendations', productId],
    queryFn: () => productApi.listRecommendations(productId),
    staleTime: 30_000,
  });
  const candidates = useQuery({
    queryKey: ['merchant-product-recommendation-candidates', search],
    queryFn: () =>
      productApi.list({ limit: 100, ...(search.trim() ? { name: search.trim() } : {}) }),
    enabled: open,
  });
  const save = useMutation({
    mutationFn: (ids: string[]) =>
      productApi.updateRecommendations(productId, { recommended_product_ids: ids }),
    onSuccess: async () => {
      setOpen(false);
      toast.success(t('menu.product.form.recommendations.saved'));
      // PUT responses may contain only `data: {}`. Re-read the canonical list
      // instead of replacing the UI with the PUT parser's default empty list.
      const updated = await queryClient.fetchQuery({
        queryKey: ['merchant-product-recommendations', productId],
        queryFn: () => productApi.listRecommendations(productId),
        staleTime: 0,
      });
      setSelected(updated.list);
    },
    onError: (error) =>
      toast.error(
        getApiErrorMessage(error, t('menu.product.form.recommendations.error')),
      ),
  });

  const current = selected ?? recommendations.data?.list ?? [];
  const currentIds = useMemo(
    () => new Set(current.map((item) => item.product_id)),
    [current],
  );
  const available = (candidates.data?.list ?? []).filter(
    (item) => item.id !== productId && !currentIds.has(item.id),
  );

  function add(item: (typeof available)[number]) {
    if (current.length >= MAX_RECOMMENDATIONS) return;
    setSelected([
      ...current,
      {
        product_id: item.id,
        name: item.name,
        image: item.images[0]?.url ?? '',
        price: item.price ?? 0,
        effective_price: item.effective_price ?? item.price ?? 0,
        measure_amount: item.measure_amount ?? 0,
        measurement_type: item.measurement_type ?? 'MEASUREMENT_TYPE_UNSPECIFIED',
        status: item.status,
        requires_selection: false,
      },
    ]);
  }

  return (
    <section className="flex flex-col gap-3 rounded-3xl bg-muted p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">
            {t('menu.product.form.recommendations.title')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('menu.product.form.recommendations.count', { count: current.length })}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          disabled={current.length >= MAX_RECOMMENDATIONS}
          className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
        >
          {open
            ? t('menu.product.form.recommendations.close')
            : t('menu.product.form.recommendations.add')}
        </button>
      </div>

      {recommendations.isLoading && (
        <p className="text-sm text-muted-foreground">Yuklanmoqda…</p>
      )}
      <div className="grid gap-2 sm:grid-cols-2">
        {current.map((item) => (
          <div
            key={item.product_id}
            className="flex items-center gap-3 rounded-2xl bg-card p-2"
          >
            {item.image ? (
              <img src={item.image} alt="" className="size-12 rounded-xl object-cover" />
            ) : (
              <div className="size-12 rounded-xl bg-muted" />
            )}
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {item.name || item.product_id}
            </span>
            <button
              type="button"
              aria-label={t('menu.product.form.recommendations.remove')}
              onClick={() =>
                setSelected(
                  current.filter((entry) => entry.product_id !== item.product_id),
                )
              }
            >
              <XIcon className="size-4" />
            </button>
          </div>
        ))}
      </div>

      {open && (
        <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-3">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('menu.product.form.recommendations.search')}
            className="h-10 rounded-xl border border-input bg-background px-3 text-sm"
          />
          <div className="max-h-64 overflow-y-auto">
            {available.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => add(item)}
                className="flex w-full items-center gap-3 border-b border-border py-2 text-left last:border-0"
              >
                {item.images[0]?.url ? (
                  <img
                    src={item.images[0].url}
                    alt=""
                    className="size-10 rounded-lg object-cover"
                  />
                ) : (
                  <div className="size-10 rounded-lg bg-muted" />
                )}
                <span className="flex-1 truncate text-sm">{item.name}</span>
                <span className="text-xs text-muted-foreground">
                  {item.effective_price ?? item.price ?? 0}
                </span>
              </button>
            ))}
            {!available.length && (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {t('menu.product.form.recommendations.empty')}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => save.mutate(current.map((item) => item.product_id))}
            disabled={save.isPending}
            className="h-11 rounded-full bg-primary text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            {save.isPending
              ? t('menu.product.form.recommendations.saving')
              : t('menu.product.form.recommendations.save')}
          </button>
        </div>
      )}
    </section>
  );
}
